import { Controller } from "@hotwired/stimulus"

const stylesheets = new Map()
const INITIALIZATION_TIMEOUT_MS = 15_000

function loadStylesheet(url) {
  const absoluteUrl = new URL(url, document.baseURI).href
  const cached = stylesheets.get(absoluteUrl)
  if (cached?.link.isConnected) return cached
  cached?.cancel()

  const link = document.createElement("link")
  const stylesheet = { link }
  stylesheet.promise = new Promise((resolve, reject) => {
    let settled = false
    const finish = (error) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      link.onload = link.onerror = null
      if (error) {
        link.remove()
        if (stylesheets.get(absoluteUrl) === stylesheet) stylesheets.delete(absoluteUrl)
        reject(error)
      } else {
        resolve()
      }
    }
    stylesheet.cancel = () => finish(new Error("Photo viewer styles unavailable"))
    const timeout = setTimeout(stylesheet.cancel, INITIALIZATION_TIMEOUT_MS)
    link.rel = "stylesheet"
    link.dataset.documentGalleryStyle = ""
    link.href = absoluteUrl
    link.onload = () => finish()
    link.onerror = stylesheet.cancel
  })
  stylesheets.set(absoluteUrl, stylesheet)
  document.head.append(link)
  return stylesheet
}

export default class extends Controller {
  static targets = ["notice"]
  static values = { moduleUrl: String, stylesheetUrl: String }

  connect() {
    this.generation = (this.generation || 0) + 1
    this.connected = true
    this.opened = false
    this.accessDenied = false
    this.frameAwaitingRender = false
    this.noticeTarget.textContent = ""
  }

  disconnect() {
    this.connected = false
    this.teardown()
  }

  async open(event) {
    let trigger = event.target.closest("[data-document-gallery-item]")
    if (!trigger || !this.element.contains(trigger) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    if (this.opened || this.accessDenied) return
    const generation = ++this.generation
    const frame = trigger.closest("turbo-frame")
    if (this.navigationBusy(frame)) {
      const documentId = trigger.dataset.documentId
      this.noticeTarget.textContent = "Documents are updating. Opening this photo when they finish…"
      // A promoted frame visit caches the page after rendering the frame. Turbo
      // removes temporary overlays during that cache event, so snapshot/open
      // only after both the frame and the promoted visit have settled.
      if (!await this.waitForNavigation(frame, generation)) return
      trigger = this.photoLink(documentId)
      if (!trigger) {
        this.noticeTarget.textContent = "That photo is no longer in these results."
        return
      }
    }
    const links = Array.from(this.element.querySelectorAll("[data-document-gallery-item]"))
    const items = links.map((link) => Object.freeze({
      src: link.dataset.displayUrl, msrc: link.dataset.thumbnailUrl,
      width: Number(link.dataset.displayWidth), height: Number(link.dataset.displayHeight),
      alt: link.dataset.documentName, original: link.href
    }))
    const hasNextPage = trigger.closest("[data-document-gallery-has-next-page]").dataset.documentGalleryHasNextPage === "true"
    this.opened = true
    this.noticeTarget.textContent = "Opening photo viewer…"
    this.emit("open")
    try {
      const module = await this.loadViewer()
      if (!this.connected || generation !== this.generation) return
      this.noticeTarget.textContent = ""
      this.viewer = module.openDocumentGallery({
        items: Object.freeze(items), index: links.indexOf(trigger), hasNextPage,
        onClose: ({ returnFocus }) => {
          if (generation !== this.generation) return
          this.viewer = null
          this.opened = false
          this.emit("close")
          if (returnFocus) this.restoreFocus(trigger, generation)
        },
        onAccessDenied: () => {
          this.accessDenied = true
          this.noticeTarget.textContent = "Your access changed. Sign in again to continue viewing photos."
          this.emit("access-denied")
        }
      })
    } catch (_) {
      if (!this.connected || generation !== this.generation) return
      this.noticeTarget.textContent = "The photo viewer could not open. You can still use Download original."
      this.opened = false
      this.emit("close")
      if (trigger.isConnected) trigger.focus({ preventScroll: true })
    }
  }

  async loadViewer() {
    const stylesheet = loadStylesheet(this.stylesheetUrlValue)
    let cancelDeadline
    const deadline = new Promise((_, reject) => {
      cancelDeadline = () => reject(new Error("Photo viewer initialization timed out"))
    })
    const timeout = setTimeout(cancelDeadline, INITIALIZATION_TIMEOUT_MS)
    const initialization = {
      cancel: () => {
        clearTimeout(timeout)
        stylesheet.cancel()
        cancelDeadline()
      }
    }
    this.initialization = initialization
    try {
      const [module] = await Promise.race([
        Promise.all([import(this.moduleUrlValue), stylesheet.promise]), deadline
      ])
      return module
    } finally {
      initialization.cancel()
      if (this.initialization === initialization) this.initialization = null
    }
  }

  frameRequestStarted(event) {
    if (event.target.closest("turbo-frame")?.id === "claim_documents") this.frameAwaitingRender = true
  }

  frameRequestFinished(event) {
    if (event.target.closest("turbo-frame")?.id === "claim_documents") this.frameAwaitingRender = false
  }

  navigationBusy(frame) {
    return this.frameAwaitingRender || frame?.hasAttribute("busy") || document.documentElement.hasAttribute("aria-busy")
  }

  async waitForNavigation(frame, generation) {
    do {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      if (!this.connected || generation !== this.generation) return false
    } while (this.navigationBusy(frame))
    return true
  }

  photoLink(documentId) {
    return Array.from(this.element.querySelectorAll("[data-document-gallery-item]"))
      .find((link) => link.dataset.documentId === documentId)
  }

  async restoreFocus(trigger, generation) {
    const frame = trigger.closest("turbo-frame")
    if (trigger.isConnected) trigger.focus({ preventScroll: true })
    // Closing can release a frame refresh deferred by an upload. Keep focus on
    // the same document after replacement, unless the user moved it elsewhere.
    if (!this.navigationBusy(frame) || !await this.waitForNavigation(frame, generation)) return
    if (document.activeElement !== document.body && document.activeElement !== trigger) return
    const replacement = this.photoLink(trigger.dataset.documentId) || frame?.querySelector("#document-results-heading")
    replacement?.focus({ preventScroll: true })
  }

  beforeCache() {
    // A queued inspection has not created a viewer or frozen the frame yet.
    // Preserve that intent through a frame's history snapshot. Full page
    // before-render and disconnect still cancel it through teardown().
    if (this.opened) this.teardown()
  }

  teardown() {
    ++this.generation
    this.initialization?.cancel()
    this.viewer?.destroy()
    this.viewer = null
    if (this.opened) {
      this.opened = false
      this.emit("close")
    }
    if (this.hasNoticeTarget && !this.accessDenied) this.noticeTarget.textContent = ""
  }

  emit(name) { document.dispatchEvent(new CustomEvent(`document-gallery:${name}`)) }
}
